import { SortDirection } from '@tanstack/react-table'
import { VariantProps, cva } from 'class-variance-authority'
import { m } from 'motion/react'
import * as React from 'react'

import { cn } from '../utils'

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    // <div className="relative w-full overflow-auto">
    <table ref={ref} className={cn('w-full caption-bottom', className)} {...props} />
    // </div>
  ),
)
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('[&_th]:pb-1 [&_th]:align-bottom [&_tr]:border-b', className)}
    {...props}
  />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('text-primary-foreground font-medium', className)}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn('data-[state=selected]:bg-muted border-b transition-colors', className)}
    {...props}
  />
))
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'text-muted-foreground h-12 p-4 text-left font-medium [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const tableCellVariants = cva('align-middle [&:has([role=checkbox])]:pr-0', {
  variants: {
    type: {
      default: '',
      number: 'font-mono align-left',
    },
    size: {
      compact: 'px-4 py-2',
      default: 'p-4',
    },
  },
  defaultVariants: {
    type: 'default',
    size: 'default',
  },
})

type TableCellProps = VariantProps<typeof tableCellVariants> &
  React.TdHTMLAttributes<HTMLTableCellElement>

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, type, size, ...props }, ref) => (
    <td ref={ref} className={tableCellVariants({ type, size, className })} {...props} />
  ),
)
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground mt-4 text-sm', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

const paths = [
  'M11.5361 10.2745C11.8024 10.0029 11.8249 9.56807 11.5762 9.26961C11.3275 8.97139 10.8961 8.91526 10.5811 9.12801L10.5195 9.17391L8.00001 11.2735L5.48048 9.17391L5.41895 9.12801C5.10388 8.91526 4.67252 8.97139 4.42384 9.26961C4.17512 9.56807 4.19758 10.0029 4.46387 10.2745L4.51954 10.3263L7.51954 12.8263C7.79767 13.058 8.20234 13.058 8.48048 12.8263L11.4805 10.3263L11.5361 10.2745Z',
  'M11.5361 5.72559C11.8024 5.99718 11.8249 6.43202 11.5762 6.73047C11.3275 7.0287 10.8961 7.08482 10.5811 6.87208L10.5195 6.82618L8.00001 4.72657L5.48048 6.82618L5.41895 6.87208C5.10388 7.08482 4.67252 7.0287 4.42384 6.73047C4.17512 6.43202 4.19758 5.99718 4.46387 5.72559L4.51954 5.67383L7.51954 3.17383C7.79767 2.94206 8.20234 2.94206 8.48048 3.17383L11.4805 5.67383L11.5361 5.72559Z',
]

const TableSortIndicator = (props: { sort: SortDirection | false }) => {
  const animatePath = React.useMemo(() => {
    return props.sort === 'asc' ? paths[0] : props.sort === 'desc' ? paths[1] : paths[0]
  }, [props.sort])

  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      role="img"
      focusable="false"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      className="group-data-[sorting=true]:fill-foreground fill-accent-foreground opacity-0 group-hover:opacity-100 group-data-[sorting=true]:opacity-100"
    >
      <m.path animate={{ d: animatePath }} d={paths[0]} />
      <path d="M8.75 12.25C8.75 12.6642 8.41421 13 8 13C7.58579 13 7.25 12.6642 7.25 12.25L7.25 3.75C7.25 3.33579 7.58579 3 8 3C8.41421 3 8.75 3.33579 8.75 3.75V12.25Z"></path>
    </svg>
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableSortIndicator,
}
